import { mockRobotState } from '@/views/my-patients/notification-tabs/mockData';
import { transSericeApolloClient } from '@/graphql/client';
import { GET_TASKS, GET_TASKS_BY_CLINIC } from '@/graphql/queries/my-calendar';
import moment from 'moment-timezone';
import store from '@/store';
import workflowService from '@/services/workflow';
import { getJwtTokenCustomerListApi } from '@/api/customer';
import { getInternalNotesMentionsApi } from '@/api/internalNotes';

async function requestNotificationCenter(config) {
  return workflowService(config);
}

const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

export async function getRobotStateApi() {
  await delay(200);
  return { data: mockRobotState };
}

// Avatar color palette for mention items
const AVATAR_COLORS = [
  { bg: '#FCDDEC', color: '#EF5DA8' },
  { bg: '#E1D3F1', color: '#6F4999' },
  { bg: '#E4F3F5', color: '#1C96AD' },
  { bg: '#E8DDB5', color: '#AEA585' },
  { bg: '#C6E0B3', color: '#779968' },
  { bg: '#FFE697', color: '#CB9F12' },
  { bg: '#F4D7D4', color: '#D18B84' },
  { bg: '#E0E0E0', color: '#9A9A9A' },
];

function getAvatarColor(id) {
  const hash = String(id).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getInitials(firstName, lastName) {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return (f + l) || '?';
}

function getCustomerName(customerId) {
  const customer = store.getters['myPatients/practiceInfo/getCustomerById']?.(customerId)
    || store.state.myPatients?.practiceInfo?.customers?.[customerId];
  if (customer) {
    const first = customer.customer_first_name || '';
    const last = customer.customer_last_name || '';
    return { firstName: first, lastName: last, fullName: [first, last].filter(Boolean).join(' ') || `Provider ${customerId}` };
  }
  return { firstName: '', lastName: '', fullName: `Provider ${customerId}` };
}

function getPatientNameFromRooms(patientId) {
  const roomList = store.state.myPatients?.rooms?.roomList || [];
  const room = roomList.find(r => String(r.patientBasicInfo?.patientId) === String(patientId));
  if (room) return room.roomName || '';
  return '';
}

/**
 * Transform @doctor_CUSTOMERID_CLINICID mentions in raw content
 * to <mention>@ProviderName</mention> tags for MentionItem rendering.
 */
function transformMentionsContent(content) {
  if (!content) return '';
  // Escape HTML first to prevent XSS
  let safe = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Replace @doctor_CUSTOMERID_CLINICID with <mention>@Name</mention>
  safe = safe.replace(/@doctor_(\w+)_\w+/g, (_match, customerId) => {
    const customer = getCustomerName(customerId);
    return `<mention>@${customer.fullName}</mention>`;
  });
  return safe;
}

function mapMentionNote(note) {
  const author = getCustomerName(note.author_customer_id);
  const avatarColor = getAvatarColor(note.author_customer_id);
  const patientName = getPatientNameFromRooms(note.patient_id);
  const displayContent = transformMentionsContent(note.content);
  return {
    id: note.id,
    noteId: note.id,
    patientId: String(note.patient_id),
    replyCount: note.reply_count || 0,
    initials: getInitials(author.firstName, author.lastName),
    name: author.fullName,
    isNew: !!note.is_unread,
    timeAgo: formatTimeAgo(note.created_at),
    patientName,
    message: `"${displayContent}"`,
    avatarBg: avatarColor.bg,
    avatarColor: avatarColor.color,
    _raw: note,
  };
}

export async function getMentionsApi({ page = 1, perPage = 10 } = {}) {
  const response = await getInternalNotesMentionsApi({ page, limit: perPage });
  const notes = response?.data || [];
  const internalNotes = notes.map(mapMentionNote);
  const totalCount = response?.meta?.total_count ?? internalNotes.length;
  const unreadCount = response?.meta?.unread_count ?? 0;
  return { data: { chat: [], internalNotes, totalCount, unreadCount } };
}

// --- Guardian Agent API ---

export async function getGuardianSessionsApi(clinicId) {
  const params = {};
  if (clinicId) params.clinicId = clinicId;
  const { data } = await requestNotificationCenter({
    url: '/guardian/sessions',
    method: 'get',
    params,
  });
  return { data: data?.data || data };
}

export async function getGuardianMessagesApi(sessionId) {
  const { data } = await requestNotificationCenter({
    url: `/guardian/sessions/${sessionId}/messages`,
    method: 'get',
  });
  return { data: data?.data || data };
}

export async function sendGuardianMessageApi(sessionId, content) {
  const { data } = await requestNotificationCenter({
    url: `/guardian/sessions/${sessionId}/messages`,
    method: 'post',
    data: { content },
  });
  return { data: data?.data || data };
}

export async function archiveGuardianSessionApi(sessionId) {
  const { data } = await requestNotificationCenter({
    url: `/guardian/sessions/${sessionId}/archive`,
    method: 'put',
  });
  return { data: data?.data || data };
}

// --- Guardian data mappers ---

function mapSeverityToPriority(severity) {
  if (severity === 'high') return 'error';
  return 'warning';
}

export function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const tz = store.state.calendar?.calendarTimezone || moment.tz.guess();
  const m = moment.utc(dateStr).tz(tz);
  return m.isValid() ? m.fromNow() : '';
}

export function mapSessionToItem(session) {
  return {
    id: session.id,
    title: session.workflow_name || '',
    priority: mapSeverityToPriority(session.severity),
    timeAgo: session.updated_at ? `Updated ${formatTimeAgo(session.updated_at)}` : formatTimeAgo(session.created_at),
    patientName: session.patient_name || '',
    description: session.last_message || '',
    chatHistory: null,
    _raw: {
      instanceId: session.instance_id,
      nodeId: session.node_id,
      status: session.status,
      readAt: session.read_at,
      patientId: session.patient_id,
      severityScore: session.severity_score,
    },
  };
}

export async function getOrdersIssueTypesApi() {
  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/issue-types',
    method: 'get',
  });

  const types = Array.isArray(data) ? data : [];
  return {
    data: types.map(t => ({
      value: t === 'All' ? 'all' : t,
      label: t,
    })),
  };
}

export async function getOrdersProviderOptionsApi() {
  const { data } = await getJwtTokenCustomerListApi();

  const options = Array.isArray(data)
    ? data
      .map(customerObj => {
        const customerId = Object.keys(customerObj || {})[0];
        const customerName = Object.values(customerObj || {})[0];
        if (!customerId || !customerName) return null;
        return {
          value: String(customerId),
          label: String(customerName),
        };
      })
      .filter(Boolean)
    : [];

  return {
    data: [{ value: 'all', label: 'All' }, ...options],
  };
}

export function mapMessageToChatItem(msg) {
  if (msg.role === 'provider') {
    return { type: 'provider', text: msg.content };
  }
  return { type: 'robot', text: msg.content };
}

function formatDueDate(dateStr) {
  if (!dateStr) return 'N/A';
  const tz = store.state.calendar?.calendarTimezone || moment.tz.guess();
  const date = moment.utc(dateStr).tz(tz);
  if (!date.isValid()) return 'N/A';

  if (date.hour() === 23 && date.minute() === 59 && date.second() === 59) {
    return date.format('ddd, MMM D');
  }

  return date.format('ddd, MMM D, h:mma');
}

function mapTask(task) {
  const tz = store.state.calendar?.calendarTimezone || moment.tz.guess();
  const taskDueDate = task.due_date;
  const isOverdue = !task.is_completed && taskDueDate &&
    moment().tz(tz).isAfter(moment.utc(taskDueDate).tz(tz).endOf('day'));
  return {
    ...task,
    id: task.task_id,
    task: task.title,
    completed: task.is_completed,
    dueDate: formatDueDate(taskDueDate),
    overdue: isOverdue,
  };
}

export async function getTasksApi({ filter = 'all', page = 1, perPage = 10 } = {}) {
  const isClinic = filter === 'all';
  const query = isClinic ? GET_TASKS_BY_CLINIC : GET_TASKS;

  const { data } = await transSericeApolloClient.query({
    query,
    fetchPolicy: 'network-only',
  });

  let rawItems;
  let openedTaskCount = null;
  if (isClinic) {
    rawItems = data.tasksByClinic?.tasks || [];
    openedTaskCount = data.tasksByClinic?.openedTaskCount ?? null;
  } else {
    rawItems = data.tasksByCreator || [];
  }

  let items = rawItems.filter(t => t.is_active !== false).map(mapTask);
  const start = (page - 1) * perPage;
  const paged = items.slice(start, start + perPage);
  return { data: { items: paged, total: items.length, openedTaskCount } };
}

const STATUS_LABEL_MAP = {
  // Issues
  'create-issue': 'Created Issue',
  'resolve-issue': 'Resolved Issue',
  'unresolve-issue': 'Unresolved Issue',
  'update-issue': 'Updated Issue',
  'comment-issue': 'Commented Issue',
  'delete-issue': 'Deleted Issue',
  // Reports
  'report_available': 'Report Available',
  'report_updated': 'Report Updated',
  // Operations - order
  'new_order': 'New Order',
  'order_edit': 'Order Edit',
  'order_payout': 'Order Payout',
  'order_redraw': 'Order Redraw',
  'order_canceled': 'Order Canceled',
  'payment_complete': 'Payment Complete',
  'payment_failed': 'Payment Failed',
  'payment_refund': 'Payment Refund',
  'test_not_performed': 'Test Not Performed',
  // Operations - shipping
  'lab_shipped': 'Lab Shipped',
  'patient_received': 'Patient Received',
  'sample_shipped_back': 'Sample Shipped Back',
  'lab_received': 'Lab Received',
};

function toStatusLabel(status) {
  if (!status) return '';
  const key = String(status).toLowerCase();
  if (STATUS_LABEL_MAP[key]) return STATUS_LABEL_MAP[key];
  // Fallback: capitalize words split by _ or -
  return String(status)
    .split(/[_-]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function toStatusType(status) {
  return String(status || 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function formatEventTime(eventTime) {
  if (!eventTime) return 'N/A';
  const tz = store.state.calendar?.calendarTimezone || moment.tz.guess();
  const m = moment.utc(eventTime).tz(tz);
  return m.isValid() ? m.fromNow() : 'N/A';
}

function formatServiceDate(serviceDate) {
  if (!serviceDate) return 'N/A';
  const tz = store.state.calendar?.calendarTimezone || moment.tz.guess();
  const m = moment.utc(serviceDate).tz(tz);
  return m.isValid() ? m.format('MM/DD/YY') : 'N/A';
}

function mapOrderItem(item = {}) {
  return {
    id: item.id,
    eventTime: item.event_time,
    updatedTime: item.updated_time,
    statusRaw: item.status,
    status: toStatusLabel(item.status),
    statusType: toStatusType(item.status),
    issueType: item.issue_type || null,
    providerId: item.provider_id,
    providerName: item.provider_name,
    patientName: item.patient_name,
    accessionId: item.accession_id,
    serviceDateRaw: item.service_date,
    serviceDate: formatServiceDate(item.service_date),
    read: Boolean(item.read),
    archived: Boolean(item.archived),
    time: formatEventTime(item.event_time),
  };
}

export async function getOrdersTabsApi({ patientId } = {}) {
  const params = {};
  if (patientId) {
    params.patient_id = patientId;
  }

  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/tabs',
    method: 'get',
    params,
  });

  return {
    data: {
      tabs: data?.tabs || [],
      ordersBadge: data?.orders_badge || { unread: 0, total: 0 },
      partialErrors: data?.partial_errors || [],
    },
  };
}

function normalizeStatusList(statusList) {
  if (!statusList) return [];
  if (Array.isArray(statusList)) {
    return statusList.filter(Boolean);
  }
  return [statusList].filter(Boolean);
}

function normalizeLimit(limit) {
  const parsed = Number(limit);
  if (!Number.isFinite(parsed)) return 20;
  return Math.min(Math.max(parsed, 1), 100);
}

function buildOrdersQueryParams({ tab, patientId, operationType, providerName, issueType, timeframe, statusList, cursor = '', limit = 20 } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(normalizeLimit(limit)));

  if (tab) {
    params.set('tab', tab);
  }
  if (patientId) {
    params.set('patient_id', patientId);
  }
  if (operationType) {
    params.set('operation_type', operationType);
  }
  if (providerName) {
    params.set('provider_name', providerName);
  }
  if (issueType) {
    params.set('issue_type', issueType);
  }
  if (timeframe) {
    params.set('timeframe', timeframe);
  }

  normalizeStatusList(statusList).forEach(status => {
    params.append('status_list', status);
  });

  if (cursor) {
    params.set('cursor', cursor);
  }

  return params;
}

export async function getOrdersApi({ tab, patientId, operationType, providerName, issueType, timeframe, statusList, cursor = '', limit = 20 } = {}) {
  const params = buildOrdersQueryParams({
    tab,
    patientId,
    operationType,
    providerName,
    issueType,
    timeframe,
    statusList,
    cursor,
    limit,
  });

  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/items',
    method: 'get',
    params,
  });

  return {
    data: {
      items: Array.isArray(data?.items) ? data.items.map(mapOrderItem) : [],
      nextCursor: data?.next_cursor || '',
      hasMore: Boolean(data?.has_more),
      total: data?.total ?? null,
      unread: data?.unread ?? 0,
      partialErrors: data?.partial_errors || [],
    },
  };
}

export async function getArchivedOrdersApi({ tab, patientId, operationType, providerName, issueType, timeframe, statusList, cursor = '', limit = 20 } = {}) {
  const params = buildOrdersQueryParams({
    tab,
    patientId,
    operationType,
    providerName,
    issueType,
    timeframe,
    statusList,
    cursor,
    limit,
  });

  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/items/archived',
    method: 'get',
    params,
  });

  return {
    data: {
      items: Array.isArray(data?.items) ? data.items.map(mapOrderItem) : [],
      nextCursor: data?.next_cursor || '',
      hasMore: Boolean(data?.has_more),
      total: data?.total ?? null,
      partialErrors: data?.partial_errors || [],
    },
  };
}

export async function batchReadOrdersApi(ids) {
  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/items/batch-read',
    method: 'post',
    data: { ids },
  });
  return { data };
}

export async function batchArchiveOrdersApi(ids) {
  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/items/batch-archive',
    method: 'post',
    data: { ids },
  });
  return { data };
}

export async function batchUnarchiveOrdersApi(ids) {
  const { data } = await requestNotificationCenter({
    url: '/notification-center/orders/items/batch-unarchive',
    method: 'post',
    data: { ids },
  });
  return { data };
}

export async function markOrderReadApi(id) {
  await requestNotificationCenter({
    url: `/notification-center/orders/items/${id}/read`,
    method: 'patch',
  });
}

export async function archiveOrderApi(id) {
  await requestNotificationCenter({
    url: `/notification-center/orders/items/${id}/archive`,
    method: 'patch',
  });
}
